import { useState } from 'react';
import './Agendamento.css';

const parseDuration = (durationStr) => {
    if (!durationStr) return 60;
    let totalMinutes = 0;
    const hoursMatch = durationStr.match(/(\d+)\s*h/);
    const minsMatch = durationStr.match(/(\d+)\s*min/);
    if (hoursMatch) totalMinutes += parseInt(hoursMatch[1]) * 60;
    if (minsMatch) totalMinutes += parseInt(minsMatch[1]);
    if (totalMinutes === 0) {
        const numMatch = durationStr.match(/(\d+)/);
        totalMinutes = numMatch ? parseInt(numMatch[1]) : 60;
    }
    return totalMinutes;
};

const AgendamentoDataHora = ({ onConfirm, onBack, selectedBarber, selectedServices, appointments, blockedTimes = [], services = [], reschedulingAppId = null }) => {
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTime, setSelectedTime] = useState('');
    const [currentDate, setCurrentDate] = useState(new Date());

    const totalRequiredMinutes = selectedServices.reduce((total, s) => total + parseDuration(s.duration), 0);

    const timeToMinutes = (timeStr) => {
        const [h, m] = timeStr.split(':').map(Number);
        return h * 60 + m;
    };

    const isTimeTaken = (time, dayConfig) => {
        const now = new Date();
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const slotStartMins = timeToMinutes(time);
        const slotEndMins = slotStartMins + totalRequiredMinutes;

        // Block past times if the date is today
        if (selectedDate === todayStr) {
            const currentMins = now.getHours() * 60 + now.getMinutes();
            if (slotStartMins <= currentMins) return true;
        }

        // Bloquear se estiver no intervalo de almoço do barbeiro
        if (dayConfig?.lunchActive) {
            const lunchStartMins = timeToMinutes(dayConfig.lunchStart);
            const lunchEndMins = timeToMinutes(dayConfig.lunchEnd);
            // Sobreposição: (InicioSlot < FimAlmoco) && (FimSlot > InicioAlmoco)
            if ((slotStartMins < lunchEndMins) && (slotEndMins > lunchStartMins)) {
                return true;
            }
        }

        // Verificar se o intervalo do agendamento [slotStart, slotEnd] sobrepõe algum existente
        const hasConflict = appointments.some(app => {
            if (app.id === reschedulingAppId) return false;
            if (app.barber !== selectedBarber.name || app.date !== selectedDate || app.status === 'Cancelado') return false;

            const appStart = timeToMinutes(app.time);

            // Lógica de fallback para duração em agendamentos legados
            let appDuration = app.durationMinutes;
            if (!appDuration) {
                const serviceNames = app.service.split(' + ');
                appDuration = serviceNames.reduce((acc, name) => {
                    const srv = services.find(s => s.name === name);
                    return acc + (srv ? parseDuration(srv.duration) : 0);
                }, 0);
                if (appDuration === 0) appDuration = 30; // Fallback final se nada for encontrado
            }

            const appEnd = appStart + appDuration;

            // Sobreposição: (Inicio1 < Fim2) && (Fim1 > Inicio2)
            return (slotStartMins < appEnd) && (slotEndMins > appStart);
        });

        if (hasConflict) return true;

        const hasBlockedConflict = blockedTimes.some(block => {
            if (block.date !== selectedDate) return false;
            if (block.barber !== selectedBarber.name && block.barber !== 'Todos' && block.barber !== 'SHOP') return false;

            const blockStart = timeToMinutes(block.time);
            const blockEnd = blockStart + (block.durationMinutes || 5); // Bloqueios manuais agora 5min por padrão

            return (slotStartMins < blockEnd) && (slotEndMins > blockStart);
        });

        return hasBlockedConflict;
    };

    // Gerar horários dinamicamente baseado no barbeiro selecionado e na duração total
    const generateTimes = () => {
        if (!selectedDate || !selectedBarber) return [];

        const dayNamesShort = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
        const dateParts = selectedDate.split('-');
        const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
        const dayKey = dayNamesShort[dateObj.getDay()];

        const wh = selectedBarber.workingHours?.[dayKey];
        if (!wh || !wh.active) return [];

        const slots = [];
        const startTotalMins = timeToMinutes(wh.start);
        const endTotalMins = timeToMinutes(wh.end);

        // Intervalo dinâmico: se o serviço dura 20min, os slots pulam de 20 em 20.
        // Se houver um agendamento no meio, o próximo slot "cola" no fim do agendamento.
        let currentMins = startTotalMins;

        while (currentMins + totalRequiredMinutes <= endTotalMins) {
            const timeStr = `${Math.floor(currentMins / 60).toString().padStart(2, '0')}:${(currentMins % 60).toString().padStart(2, '0')}`;

            if (!isTimeTaken(timeStr, wh)) {
                slots.push(timeStr);
                currentMins += totalRequiredMinutes; // Pula para o próximo slot livre após este
            } else {
                // Se o horário está ocupado, precisamos pular para o final do agendamento ou almoço que está bloqueando

                // Verificar Almoço primeiro como causa de bloqueio
                if (wh.lunchActive) {
                    const lStart = timeToMinutes(wh.lunchStart);
                    const lEnd = timeToMinutes(wh.lunchEnd);
                    if ((currentMins < lEnd) && (currentMins + totalRequiredMinutes > lStart)) {
                        currentMins = lEnd;
                        continue;
                    }
                }

                const conflictApp = appointments.find(app => {
                    if (app.id === reschedulingAppId) return false;
                    if (app.barber !== selectedBarber.name || app.date !== selectedDate || app.status === 'Cancelado') return false;
                    const appStart = timeToMinutes(app.time);
                    let appDuration = app.durationMinutes;
                    if (!appDuration) {
                        const serviceNames = (app.service || "").split(' + ');
                        appDuration = serviceNames.reduce((acc, name) => {
                            const srv = services.find(s => s.name === name);
                            return acc + (srv ? parseDuration(srv.duration) : 0);
                        }, 0);
                        if (appDuration === 0) appDuration = 30;
                    }
                    const appEnd = appStart + appDuration;
                    return (currentMins < appEnd) && (currentMins + totalRequiredMinutes > appStart);
                });

                if (conflictApp) {
                    const appStart = timeToMinutes(conflictApp.time);
                    let appDuration = conflictApp.durationMinutes;
                    if (!appDuration) {
                        const serviceNames = (conflictApp.service || "").split(' + ');
                        appDuration = serviceNames.reduce((acc, name) => {
                            const srv = services.find(s => s.name === name);
                            return acc + (srv ? parseDuration(srv.duration) : 0);
                        }, 0);
                        if (appDuration === 0) appDuration = 30;
                    }
                    currentMins = appStart + appDuration;
                } else {
                    currentMins += 5; // Fallback para evitar loop infinito
                }
            }

            // Segurança para não entrar em loop infinito
            if (currentMins > endTotalMins) break;
        }
        return slots;
    };

    const times = generateTimes();

    const totalPrice = selectedServices.reduce((total, s) => {
        const val = parseFloat(s.price.replace('R$ ', '').replace(',', '.'));
        return total + val;
    }, 0);

    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);

    const monthNames = [
        "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ];

    const generateCalendarDays = () => {
        const days = [];
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
        }
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isSelected = selectedDate === dateStr;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const checkDate = new Date(year, month, day);
            const isPast = checkDate < today;
            const isClosed = appointments.some(app => app.date === dateStr && app.status === 'Sistema_Bloqueado');

            days.push(
                <button
                    key={day}
                    className={`calendar-day ${isSelected ? 'selected' : ''} ${isPast ? 'disabled' : ''} ${isClosed ? 'closed' : ''}`}
                    onClick={() => !isPast && !isClosed && setSelectedDate(dateStr)}
                    disabled={isPast || isClosed}
                    title={isClosed ? 'Barbearia Fechada' : ''}
                >
                    {day}
                </button>
            );
        }
        return days;
    };

    const changeMonth = (offset) => {
        const newDate = new Date(currentDate.setMonth(currentDate.getMonth() + offset));
        setCurrentDate(new Date(newDate));
    };

    const handleConfirm = () => {
        if (selectedDate && selectedTime) {
            onConfirm({ date: selectedDate, time: selectedTime });
        }
    };

    return (
        <div className="booking-container fade-in">
            <h2 className="booking-title">Escolha o Horário</h2>
            <p className="booking-subtitle">Compareça com 20 min de antecedência.</p>

            <div className="booking-summary">
                <p><strong>Barbeiro:</strong> {selectedBarber?.name}</p>
                <p><strong>Serviços:</strong> {selectedServices.map(s => s.name).join(' + ')}</p>
                <div className="summary-total">
                    <span>Valor Total:</span>
                    <strong>R$ {totalPrice.toFixed(2).replace('.', ',')}</strong>
                </div>
            </div>

            <div className="calendar-container">
                <div className="calendar-header">
                    <button onClick={() => changeMonth(-1)} className="nav-button">&lt;</button>
                    <h3 className="current-month">{monthNames[month]} {year}</h3>
                    <button onClick={() => changeMonth(1)} className="nav-button">&gt;</button>
                </div>

                <div className="weekdays-grid">
                    <span>Dom</span><span>Seg</span><span>Ter</span><span>Qua</span><span>Qui</span><span>Sex</span><span>Sáb</span>
                </div>

                <div className="days-grid">
                    {generateCalendarDays()}
                </div>
            </div>

            {selectedDate && (
                <div className="time-container fade-in">
                    <h3 className="section-subtitle">Horários Disponíveis</h3>
                    <div className="times-grid">
                        {times.map((time) => {
                            const taken = isTimeTaken(time);
                            return (
                                <button
                                    key={time}
                                    className={`time-button ${selectedTime === time ? 'selected' : ''} ${taken ? 'taken' : ''}`}
                                    onClick={() => !taken && setSelectedTime(time)}
                                    disabled={taken}
                                >
                                    {taken ? 'Ocupado' : time}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="action-buttons">
                <button className="back-button" onClick={onBack}>Voltar</button>
                <button
                    className="confirm-button"
                    onClick={handleConfirm}
                    disabled={!selectedDate || !selectedTime}
                >
                    FINALIZAR AGENDAMENTO
                </button>
            </div>
        </div>
    );
};

export default AgendamentoDataHora;
